<template>
  <DashboardTabbedContentBox :gridHeight="640" large>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-overview">
        <i :class="['tabbedbox-tab-icon', 'fas', 'fa-home']"></i>
        <div class="tabbedbox-tab-info">
          <div class="tabbedbox-tab-title">Overview</div>
          <div class="tabbedbox-tab-subtitle">Company Overview</div>
        </div>
      </Tab>
    </template>

    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-overview" class="tabpage-padding-0">
        <div class="greeting-box">
          <div class="greeting-top">
            <img :src="accountLogoURL" class="greeting-company-image" v-if="hasCompanyLogo" />
            <img src="glass/img/dashboard-welcome-image.png" class="greeting-image" v-if="!hasCompanyLogo" />
            <div class="greeting-header">
              <h2>{{profileCompanyName}}</h2>
              <h3>{{salutation}}, {{profileName}}</h3>
              <p class="greeting-subtitle">Here&apos;s an overview of {{profileCompanyName}} today</p>
              <div class="row no-gutters mt-4" v-if="!loading && !loadingError">
                <div class="col-2">
                  <p class="greeting-value-title">Today&apos;s orders</p>
                  <p class="greeting-value-content">{{formatInteger(summaryData.orderTotalCount, false, true)}}</p>
                </div>
                <div class="col-3">
                  <p class="greeting-value-title">Today&apos;s order sales</p>
                  <p class="greeting-value-content">{{formatCurrency(summaryData.orderTotalValue)}}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="greeting-link-list">
            <Spinner v-if="loading" />
            <div class="row no-gutters greeting-link-row" v-if="notificationUnreadCount > 0 && !loading && !loadingError">
              <div class="col-9">
                <img src="/glass/img/icon-notification.png" class="notification-img" />
                You have <strong>{{notificationUnreadCount}} unread</strong> notification{{displayCountPlural(notificationUnreadCount)}}
              </div>
              <div class="col-3 link">
                <router-link to="/communication/notifications">View Notifications <i class="far fa-chevron-right" /></router-link>
              </div>
            </div>
            <div v-for="(link, index) of quickLinkList" :key="index" :class="['row', 'no-gutters', 'greeting-link-row', link.class]">
              <div class="col-9 info">
                <i class="fas fa-circle mr-1" /> <strong>{{link.highlight}}</strong> {{link.info}}
              </div>
              <div class="col-3 link">
                <router-link :to="link.link">{{link.linkTitle}} <i class="far fa-chevron-right" /></router-link>
              </div>
            </div>
          </div>
        </div>
      </TabPage>
    </template>
  </DashboardTabbedContentBox>
</template>


<style lang="scss" scoped>
.greeting-box {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.greeting-top {
  position: relative;
  height: 220px;
}
.greeting-link-list {
  height: 350px;
  overflow-y: auto;
}

.greeting-image {
  position: absolute;
  top: 0px;
  right: 0px;
  border-top-right-radius: $layout-border-radius;
}
.greeting-company-image {
  position: absolute;
  top: 20px;
  right: 20px;
  max-width: 150px;
  max-height: 150px;
  border-top-right-radius: 6px;
}
.greeting-header {
  padding: 1rem;
}
.greeting-header > h2 {
  color: $general-highlight-color;
  font-weight: 600;
  font-size: 20px;
}
.greeting-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #888888;
}
.greeting-value-title {
  font-size: 13px;
  color: #888888;
  font-weight: 500;
  margin-bottom: 0.5rem;
}
.greeting-value-content {
  font-size: 24px;
  color: #707070;
  font-weight: 600;
}

.greeting-link-row {
  padding: 1rem;
  border-top: 1px solid #e7eaec;
}
.greeting-link-row:last-child {
  border-bottom: 1px solid #e7eaec;
}
.greeting-link-row > .info > i {
  color: $general-highlight-color;
}
.greeting-link-row > .link {
  color: $general-highlight-color;
  text-align: right;
  font-weight: 600;
}
.greeting-link-row-danger {
  background-color: rgba(237, 102, 102, 0.25);
  border-color: rgba(163, 48, 48, 0.15) !important;
}
.greeting-link-row-danger > .info > i,
.greeting-link-row-danger > .info,
.greeting-link-row-danger > .link > a {
  color: rgb(163, 48, 48);
}

.greeting-link-row-warning {
  background-color: #FFF0D7;
  border-color: rgba(194, 111, 38, 0.15) !important;
}
.greeting-link-row-warning > .info > i,
.greeting-link-row-warning > .info,
.greeting-link-row-warning > .link > a {
  color: rgb(194, 111, 38);
}

.notification-img {
  width: 24px;
  margin-right: 8px;
  margin-left: -6px;
}

</style>


<script>
import buildUrl from 'build-url'
import DateUtils from '@/utils/dateUtils'
import formMixin from '@/mixins/form'

import DashboardTabbedContentBox from '@/components/dashboard/dashboardTabbedContentBox'

import { OrderStatusTypes, PurchaseOrderStatusTypes, PricingRequestStatusTypes, TaskStatusTypes } from '@/store/types'

export default {
  name: 'DashboardGreetingBox',
  mixins: [formMixin],
  components: {
    DashboardTabbedContentBox
  },
  data() {
    return {
      loading: true,
      loadingError: '',
      summaryData: {},
      quickLinkList: []
    }
  },
  computed: {
    hasCompanyLogo() {
      return !!this.accountLogoURL
    },
    accountLogoURL() {
      return this.$store.state.profile.profileLogoURL
    },
    profileCompanyName() {
      return this.$store.state.profile.profileCompanyName
    },
    profileName() {
      return this.$store.state.profile.profileUser.name
    },
    notificationUnreadCount() {
      return this.$store.state.profile.notifications.unreadCount
    },
    salutation() {
      return DateUtils.getTimeOfDaySalutationText()
    }
  },
  mounted() {
    this.requestData()
    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      this.$apiService.coreService.getDashboardSummary()
      .then(response => {

        this.summaryData = response.data
        this.quickLinkList = []

        var orderDraftCount = this.calculateStatusTotal(this.summaryData.orderActiveStatusSummaryList, 'status', [OrderStatusTypes.draft])
        var orderAttentionCount = this.calculateStatusTotal(this.summaryData.orderActiveStatusSummaryList, 'status', [OrderStatusTypes.pendingAccountApproval, OrderStatusTypes.pendingProductionApproval, OrderStatusTypes.preProduction, OrderStatusTypes.pendingExternalSetup])
        var quotePendingSentCount = this.calculateStatusTotal(this.summaryData.orderActiveStatusSummaryList, 'status', [OrderStatusTypes.quotePendingSent])
        var orderWaitingPaymentCount = this.calculateStatusTotal(this.summaryData.orderActiveStatusSummaryList, 'status', [OrderStatusTypes.pendingPrePaid, OrderStatusTypes.pendingCODPaid])

        var orderHoldCount = this.calculateStatusTotal(this.summaryData.orderActiveHoldSummaryList, 'holdType')
        var poApprovalCount = this.calculateStatusTotal(this.summaryData.purchaseOrderActiveStatusSummaryList, 'status', [PurchaseOrderStatusTypes.pendingApproval, PurchaseOrderStatusTypes.receiving])

        // Build the quick links for important info
        if(orderAttentionCount > 0) {
          this.quickLinkList.push({
            class: 'greeting-link-row-danger',
            highlight: `${orderAttentionCount} Order${this.displayCountPlural(orderAttentionCount)}`,
            info: orderAttentionCount == 1 ? ` requires attention` : ` require attention`,
            linkTitle: 'View Orders',
            link: buildUrl('/orders', { queryParams: { filterFields: ['status'], status: [OrderStatusTypes.pendingAccountApproval, OrderStatusTypes.pendingProductionApproval, OrderStatusTypes.preProduction, OrderStatusTypes.pendingExternalSetup] } })
          })
        }
        if(poApprovalCount > 0) {
          this.quickLinkList.push({
            class: 'greeting-link-row-danger',
            highlight: `${poApprovalCount} Purchase Order${this.displayCountPlural(poApprovalCount)}`,
            info: poApprovalCount == 1 ? ` requires attention` : ` require attention`,
            linkTitle: 'View Purchase Orders',
            link: buildUrl('/purchasing/purchaseorders', { queryParams: { filterFields: ['status'], status: [PurchaseOrderStatusTypes.pendingApproval, PurchaseOrderStatusTypes.receiving] } })
          })
        }
        if(this.summaryData.priceRequestsPendingApproval > 0) {
          this.quickLinkList.push({
            class: 'greeting-link-row-danger',
            highlight: `${this.summaryData.priceRequestsPendingApproval} Price Request${this.displayCountPlural(this.summaryData.priceRequestsPendingApproval)}`,
            info: this.summaryData.priceRequestsPendingApproval == 1 ? ` requires attention` : ` require attention`,
            linkTitle: 'View Price Requests',
            link: buildUrl('/pricing/requests', { queryParams: { filterFields: ['status'], status: [PricingRequestStatusTypes.pendingApproval] } })
          })
        }
        if(orderDraftCount > 0) {
          this.quickLinkList.push({
            class: 'greeting-link-row-warning',
            highlight: `${orderDraftCount} Order${this.displayCountPlural(orderDraftCount)}`,
            info: orderDraftCount == 1 ? ` is in draft status` : ` are in draft status`,
            linkTitle: 'View Draft Orders',
            link: buildUrl('/orders', { queryParams: { filterFields: ['status'], status: [OrderStatusTypes.draft] } })
          })
        }
        if(orderHoldCount > 0) {
          this.quickLinkList.push({
            class: 'greeting-link-row-warning',
            highlight: `${orderHoldCount} Order${this.displayCountPlural(orderHoldCount)}`,
            info: orderHoldCount == 1 ? ` is on hold` : ` are on hold`,
            linkTitle: 'View Orders',
            link: buildUrl('/orders', { queryParams: { filterFields: ['isOnHold'], isOnHold: 1 } })
          })
        }
        if(orderWaitingPaymentCount > 0) {
          this.quickLinkList.push({
            class: '',
            highlight: `${orderWaitingPaymentCount} Order${this.displayCountPlural(orderWaitingPaymentCount)}`,
            info: orderWaitingPaymentCount == 1 ? ` is waiting payment` : ` are waiting payment`,
            linkTitle: 'View Orders',
            link: buildUrl('/orders', { queryParams: { filterFields: ['status'], status: [OrderStatusTypes.pendingPrePaid, OrderStatusTypes.pendingCODPaid] } })
          })
        }
        if(quotePendingSentCount > 0) {
          this.quickLinkList.push({
            class: '',
            highlight: `${quotePendingSentCount} Quote${this.displayCountPlural(quotePendingSentCount)}`,
            info: quotePendingSentCount == 1 ? ` requires sending` : ` require sending`,
            linkTitle: 'View Quotes',
            link: buildUrl('/orders', { queryParams: { filterFields: ['status'], status: [OrderStatusTypes.quotePendingSent] } })
          })
        }
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    calculateStatusTotal(list, field, statusTypes) {
      if(!list)
        return 0
      return list.reduce((curValue, info) => {
        if(!statusTypes || statusTypes.indexOf(info[field]) != -1)
          return curValue + info.totalCount
        return curValue
      }, 0)
    }
  }
}
</script>